import React from "react";
import { cn } from "@/libs/utils";
import useInterval from "src/hooks/useInterval";
import { HMS } from "src/models/Time";
import TimeUtil from "src/utils/TimeUtil";
import OrderService from "@/services/OrderService";

const TimerIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.625 9.74688V6.45834C10.625 6.28126 10.5651 6.13279 10.4452 6.01293C10.3254 5.8932 10.1769 5.83334 9.99983 5.83334C9.82261 5.83334 9.67421 5.8932 9.55462 6.01293C9.4349 6.13279 9.37504 6.28126 9.37504 6.45834V9.93918C9.37504 10.0372 9.39324 10.1322 9.42962 10.2242C9.46587 10.3161 9.52303 10.4011 9.60108 10.4792L12.4552 13.3333C12.5705 13.4488 12.7155 13.5078 12.8902 13.5104C13.065 13.5131 13.2127 13.454 13.3334 13.3333C13.4541 13.2126 13.5144 13.0663 13.5144 12.8942C13.5144 12.7222 13.4541 12.5759 13.3334 12.4552L10.625 9.74688ZM10.0015 17.9167C8.9065 17.9167 7.87726 17.7089 6.91379 17.2933C5.95032 16.8778 5.11226 16.3138 4.39962 15.6015C3.68698 14.8891 3.12275 14.0514 2.70692 13.0883C2.29122 12.1253 2.08337 11.0963 2.08337 10.0015C2.08337 8.90647 2.29115 7.87723 2.70671 6.91376C3.12226 5.95029 3.68622 5.11223 4.39858 4.39959C5.11094 3.68695 5.94865 3.12272 6.91171 2.70689C7.87476 2.29119 8.90372 2.08334 9.99858 2.08334C11.0936 2.08334 12.1228 2.29112 13.0863 2.70668C14.0498 3.12223 14.8878 3.68619 15.6005 4.39855C16.3131 5.11091 16.8773 5.94862 17.2932 6.91168C17.7089 7.87473 17.9167 8.90369 17.9167 9.99855C17.9167 11.0936 17.7089 12.1228 17.2934 13.0863C16.8778 14.0497 16.3139 14.8878 15.6015 15.6004C14.8891 16.3131 14.0514 16.8773 13.0884 17.2931C12.1253 17.7088 11.0964 17.9167 10.0015 17.9167ZM10 16.6667C11.8473 16.6667 13.4202 16.0174 14.7188 14.7188C16.0174 13.4201 16.6667 11.8472 16.6667 10C16.6667 8.15279 16.0174 6.57987 14.7188 5.28126C13.4202 3.98265 11.8473 3.33334 10 3.33334C8.15282 3.33334 6.5799 3.98265 5.28129 5.28126C3.98268 6.57987 3.33337 8.15279 3.33337 10C3.33337 11.8472 3.98268 13.4201 5.28129 14.7188C6.5799 16.0174 8.15282 16.6667 10 16.6667Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * 현재 시각(초). **매 tick 마다 실제 시계를 다시 읽는다** (#5227).
 *
 * ⚠️ 예전에는 시작 시각에 `1000` 을 누적하기만 했다(`prevTime + 1000`). 브라우저는 백그라운드
 * 탭의 `setInterval` 을 throttle 하므로(보통 분당 1회 수준) **타이머가 실제보다 느리게 갔다** —
 * 쿠키는 이미 삭제됐는데 화면에는 남은 시간이 표시되는 상태가 됐다. 절전/기기 sleep 도 같다.
 */
export function useSeconds(expireMillis: number) {
  const [millis, setMillis] = React.useState(() => Date.now());

  useInterval(
    () => {
      setMillis(Date.now());
    },
    millis < expireMillis ? 1000 : null,
  );

  return Math.floor(millis / 1000);
}

const format = (value: number): string => value.toString().padStart(2, "0");

export const View = React.memo(
  ({
    day,
    hour,
    minute,
    second,
  }: {
    day: number;
    hour: number;
    minute: number;
    second: number;
  }) => {
    return (
      <div
        className={cn(
          "flex items-center text-sm font-600 leading-none laptop:text-base",
        )}
      >
        <TimerIcon />
        <span className="ml-2" suppressHydrationWarning>
          {!!day && `D-${day.toString()} `}
          {`${format(hour % 24)}:${format(minute)}:${format(second)}`}
        </span>
      </div>
    );
  },
);
View.displayName = "View";

export const ReferralSaleTimer = () => {
  const expireMillis = OrderService.tryGetReferralEndTimeFromCookie();
  const secondsEpoch = useSeconds(expireMillis);
  const remainingSeconds = Math.floor(expireMillis / 1000 - secondsEpoch);

  if (remainingSeconds < 0) {
    return null;
  }

  const hms: HMS = TimeUtil.secondsToHMS(remainingSeconds);
  const day = Math.floor(hms.hour / 24);

  return <View day={day} {...hms} />;
};
